// The console's *Templates* area (CR-066: "TA Admin configuration without code: … templates …"; G §15 "TA Admin
// configures templates and thresholds."; B10 plan D14, slice S20b; acceptance A-38, A-11). Registered for the kind
// `template` (`template:kickoff`, `template:internal_notice.<kind>`, B5's `template:meeting.<k>`): the body is plain
// text, edited as it is; the placeholders it names are listed so a removed one is seen before Validate. The server's
// checks are the ones that count (S20a's Journey runs `config.validate`: at most 20 000 characters, no markup, and a
// V2 template's placeholders only from the registry, which never holds a restricted field class — CR-066 "validate
// against injection"). A kickoff drafted after the new version is effective carries its number (`template_version`);
// one sent before keeps its own.

import { EmptyState, Field, Flex, Stack, Tag, TextArea } from "@/mc";
import { CONSOLE_AREA_LABELS } from "./labels";
import { EditorFrame, atPath, useDraft, useEffectiveBody, type Body } from "./ThresholdsEditor";

/** The server's bound on a template body (tools/config.py TEMPLATE_MAX). */
export const TEMPLATE_MAX = 20000;

/** The placeholder grammar the server reads (`{{name}}`, `{{#name}}`, `{{/name}}`, `{{^name}}`). */
const PLACEHOLDER = /\{\{\s*[#/^]?\s*([A-Za-z0-9_.]+)\s*\}\}/g;

/** The body's text. */
export function markdownOf(body: Body | null): string {
  const markdown = atPath(body, ["markdown"]);
  return typeof markdown === "string" ? markdown : "";
}

/** A template's leading builder's note (`<!-- … -->`, the source it was copied from) and the text after it. Every reader
 *  of a template drops that note before it renders (`packet_set._render_kickoff`, `panelgov.email._template_body`:
 *  `split("-->", 1)[-1].strip()`), so the console shows only the text a recipient reads, and keeps the note in the body
 *  it saves: the note is never shown, never lost. */
export function splitNote(markdown: string): { note: string; text: string } {
  const end = markdown.trimStart().startsWith("<!--") ? markdown.indexOf("-->") : -1;
  if (end < 0) return { note: "", text: markdown };
  return { note: markdown.slice(0, end + 3), text: markdown.slice(end + 3).replace(/^\r?\n/, "") };
}

/** The body's markdown with the text edited and the note kept. */
export function joinNote(note: string, text: string): string {
  return note ? `${note}\n${text}` : text;
}

/** Every placeholder the text names, once, in the order it first appears. */
export function templatePlaceholders(markdown: string): string[] {
  const names: string[] = [];
  for (const match of markdown.matchAll(PLACEHOLDER)) if (match[1] && !names.includes(match[1])) names.push(match[1]);
  return names;
}

export function TemplateForm({ body, set }: { body: Body | null; set: (path: readonly (string | number)[], value: unknown) => void }) {
  const markdown = markdownOf(body);
  const { note, text } = splitNote(markdown);
  const names = templatePlaceholders(text);
  return (
    <Stack>
      <Field label={CONSOLE_AREA_LABELS.template} hint={<span className="mc-mono">{markdown.length} / {TEMPLATE_MAX}</span>}>
        <TextArea mono rows={18} maxLength={TEMPLATE_MAX - (note ? note.length + 1 : 0)} value={text} onChange={(event) => set(["markdown"], joinNote(note, event.target.value))}  />
      </Field>
      {names.length > 0 ? (
        <Flex wrap>{names.map((name) => <Tag key={name}><span className="mc-mono">{`{{${name}}}`}</span></Tag>)}</Flex>
      ) : null}
    </Stack>
  );
}

/** The area's editor for one `template:<name>` key (S20a's Editor contract). */
export function TemplatesEditor({ kind, configKey }: { kind: string; configKey: string }) {
  const q = useEffectiveBody(configKey);
  const draft = useDraft(q.data?.body);
  return (
    <EditorFrame
      kind={kind}
      configKey={configKey}
      title={CONSOLE_AREA_LABELS.templates}
      version={q.data?.version}
      loading={q.isLoading}
      error={q.error}
      onRetry={() => void q.refetch()}
      draft={draft.current ?? { markdown: "" }}
      onDone={() => { draft.reset(); void q.refetch(); }}
    >
      {kind === "template" ? <TemplateForm body={draft.current} set={draft.set} /> : <EmptyState inline>—</EmptyState>}
    </EditorFrame>
  );
}
